import { observer } from 'mobx-react-lite'
import { Button } from '@/components/Button'
import {
  EmptyState,
  EmptyStateActions,
  EmptyStateDescription,
  EmptyStateIconDecoration,
  EmptyStateTitle,
} from '@/components/EmptyState'
import { TextInputContainer } from '@/components/inputs/TextInputContainer'
import type { LegendEditorViewModel } from '@/features/legends/LegendEditor/LegendEditorViewModel'
import { EditorList } from '@/features/legends/components/EditorList'
import { EditorPage } from '@/features/legends/components/EditorPage'
import { SidebarMenuItem } from '@/features/legends/components/SidebarMenu'
import { firstError } from '@/features/legends/editor/validation'
import {
  Inspector,
  InspectorContent,
  InspectorHeader,
  InspectorSection,
} from '@/features/legends/components/Inspector'
import { RynoEditorContainer } from '@/features/legends/ryno/components/RynoEditor/RynoEditorContainer'
import type { TemplateEditorViewModel } from '@/features/legends/templates/TemplateEditorViewModel'
import type { TemplatesBuilderViewModel } from '@/features/legends/templates/TemplatesBuilderViewModel'
import { templateDataSources } from '@/features/legends/templates/templates'
import {
  MappingInspectorTitle,
  MappingNote,
  MappingReturnHint,
  MappingTypeToggle,
  TemplateDataSourceList,
} from '@/features/legends/templates/components/MappingInspector'
import { MappingRow } from '@/features/legends/templates/components/MappingRow'
import { TemplateEditor } from '@/features/legends/templates/components/TemplateEditor'
import { SidebarLayout } from '@/features/legends/components/SidebarLayout'
import { TemplatesSidebar } from '@/features/legends/templates/components/TemplatesSidebar'

const testId = 'legend-templates'

export interface TemplatesTabContainerProps {
  vm: LegendEditorViewModel
}

export const TemplatesTabContainer = observer(function TemplatesTabContainer({
  vm,
}: TemplatesTabContainerProps) {
  const templatesVm = vm.templatesVm
  const templateVm = templatesVm.selectedTemplateVm
  return (
    <SidebarLayout
      sidebar={<TemplatesSidebarContainer vm={templatesVm} />}
      testId={testId}
    >
      {templateVm ? (
        <EditorPage testId={`${testId}-page`}>
          <TemplateEditorContainer templatesVm={templatesVm} vm={templateVm} />
        </EditorPage>
      ) : (
        <EmptyState size="sm" grow testId={`${testId}-empty`}>
          <EmptyStateIconDecoration icon="documents" />
          <EmptyStateTitle>No template selected</EmptyStateTitle>
          <EmptyStateDescription>
            Please select a template or create a new one.
          </EmptyStateDescription>
          {!templatesVm.isEditingDisabled && (
            <EmptyStateActions>
              <Button
                label="New template"
                variant="primary"
                onPress={templatesVm.add}
                testId={`${testId}-empty-add-button`}
              />
            </EmptyStateActions>
          )}
        </EmptyState>
      )}
      {templateVm && <MappingInspectorContainer vm={templateVm} />}
    </SidebarLayout>
  )
})

interface TemplatesSidebarContainerProps {
  vm: TemplatesBuilderViewModel
}

const TemplatesSidebarContainer = observer(function TemplatesSidebarContainer({
  vm,
}: TemplatesSidebarContainerProps) {
  return (
    <TemplatesSidebar
      isEmpty={vm.templates.length === 0}
      onAdd={vm.isEditingDisabled ? undefined : vm.add}
      addTestId={`${testId}-add-button`}
    >
      {vm.templates.map((templateVm) => (
        <SidebarMenuItem
          key={templateVm.cid}
          title={templateVm.name || 'Untitled template'}
          iconName="documents"
          isSelected={vm.selectedTemplateVm === templateVm}
          error={firstError(templateVm.validation)}
          onPress={() => vm.edit(templateVm)}
          testId={`${testId}-template-${templateVm.id}`}
        />
      ))}
    </TemplatesSidebar>
  )
})

interface TemplateEditorContainerProps {
  templatesVm: TemplatesBuilderViewModel
  vm: TemplateEditorViewModel
}

const TemplateEditorContainer = observer(function TemplateEditorContainer({
  templatesVm,
  vm,
}: TemplateEditorContainerProps) {
  return (
    <TemplateEditor
      nameInput={
        <TextInputContainer
          model={vm}
          field="name"
          aria-label="Template name"
          error={vm.validation.getError('name')}
          isDisabled={vm.isEditingDisabled}
          data-test={`${testId}-name`}
        />
      }
      onDelete={vm.isEditingDisabled ? undefined : templatesVm.remove}
      deleteTestId={`${testId}-delete-button`}
      isEmpty={vm.mappings.length === 0}
    >
      <EditorList title="Questions" testId={`${testId}-mappings`}>
        {vm.mappings.map((mappingVm) => (
          <MappingRow
            key={mappingVm.cid}
            title={mappingVm.questionVm.displayText}
            questionId={mappingVm.questionId}
            description={mappingVm.questionDescription}
            type={mappingVm.type}
            isSelected={vm.selectedMappingVm === mappingVm}
            hasError={!mappingVm.validation.isValid}
            onPress={() => vm.edit(mappingVm)}
            testId={`${testId}-mapping-${mappingVm.questionId}`}
          />
        ))}
      </EditorList>
    </TemplateEditor>
  )
})

interface MappingInspectorContainerProps {
  vm: TemplateEditorViewModel
}

const MappingInspectorContainer = observer(function MappingInspectorContainer({
  vm,
}: MappingInspectorContainerProps) {
  const mappingVm = vm.selectedMappingVm
  if (!mappingVm) {
    return (
      <Inspector testId={`${testId}-inspector`}>
        <EmptyState size="sm" grow>
          <EmptyStateIconDecoration icon="list-view" />
          <EmptyStateTitle>No question selected</EmptyStateTitle>
          <EmptyStateDescription>
            Select a question to choose how it gets answered.
          </EmptyStateDescription>
        </EmptyState>
      </Inspector>
    )
  }
  return (
    <Inspector testId={`${testId}-inspector`}>
      <InspectorHeader
        title={
          <MappingInspectorTitle
            questionId={mappingVm.questionId}
            valueType={mappingVm.questionVm.valueType}
          />
        }
      />
      <InspectorContent>
        <InspectorSection>
          <MappingTypeToggle
            value={mappingVm.type}
            onChange={mappingVm.setType}
            isDisabled={mappingVm.isEditingDisabled}
            testId={`${testId}-mapping-type`}
          />
        </InspectorSection>
        {mappingVm.type === 'Manual' ? (
          <InspectorSection>
            <MappingNote>
              We will ask you for input to this question when you scope a new
              job.
            </MappingNote>
          </InspectorSection>
        ) : (
          <>
            <InspectorSection>
              <RynoEditorContainer
                vm={mappingVm.expressionVm}
                isDisabled={mappingVm.isEditingDisabled}
                aria-label="Mapping formula"
                testId={`${testId}-expression`}
              />
              <MappingReturnHint
                valueType={mappingVm.questionVm.valueType}
                possibleValues={mappingVm.possibleReturnValues}
              />
            </InspectorSection>
            <InspectorSection title="Template data">
              <MappingNote>
                Formulas can read these values from the job being scoped.
              </MappingNote>
              <TemplateDataSourceList sources={templateDataSources} />
            </InspectorSection>
          </>
        )}
      </InspectorContent>
    </Inspector>
  )
})
